import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useAdminLocale } from '../../lib/admin-locale.js';
import { useUiPrefs } from '../../lib/ui-prefs.js';

export { TABLE_PAGE_SIZE_OPTIONS } from '../../lib/ui-prefs.js';

const btn =
  'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50';
const btnPrimary = `${btn} bg-blue-600 text-white hover:bg-blue-700`;
const btnSecondary = `${btn} border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800`;

export type ListPaginationProps = {
  page: number;
  totalPages: number;
  total: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  className?: string;
};

/** Shared pager for admin tables (page size comes from platform settings). */
export function ListPagination({
  page,
  totalPages,
  total,
  itemsPerPage,
  onPageChange,
  className,
}: ListPaginationProps) {
  const { text } = useAdminLocale();
  const rangeStart = total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(page * itemsPerPage, total);
  const pages = visiblePages(page, totalPages);

  return (
    <div
      className={`mt-auto flex shrink-0 flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-slate-100 bg-white px-4 py-3 sm:px-5 dark:border-slate-800 dark:bg-slate-900 ${className ?? ''}`}
    >
      <p className="shrink-0 text-sm leading-10 text-slate-500 dark:text-slate-400">
        {text('Εμφάνιση', 'Showing')}{' '}
        <span className="font-medium text-slate-800 dark:text-slate-200">{rangeStart}</span>–
        <span className="font-medium text-slate-800 dark:text-slate-200">{rangeEnd}</span>{' '}
        {text('από', 'of')}{' '}
        <span className="font-medium text-slate-800 dark:text-slate-200">{total}</span>
        <span className="text-slate-400 dark:text-slate-500">
          {' '}
          · {itemsPerPage}/{text('σελίδα', 'page')}
        </span>
      </p>
      <nav
        className="inline-flex shrink-0 items-center gap-1"
        aria-label={text('Σελιδοποίηση', 'Pagination')}
      >
        <button
          type="button"
          disabled={page <= 1}
          aria-label={text('Προηγούμενη σελίδα', 'Previous page')}
          className={`${btnSecondary} !px-2.5 !py-1.5`}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          ‹
        </button>
        {pages.map((entry, index) =>
          entry === '…' ? (
            <span key={`e-${index}`} className="px-1 text-sm text-slate-400 dark:text-slate-500">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              aria-current={entry === page ? 'page' : undefined}
              className={`${entry === page ? btnPrimary : btnSecondary} !min-w-9 !px-2.5 !py-1.5`}
              onClick={() => onPageChange(entry)}
            >
              {entry}
            </button>
          ),
        )}
        <button
          type="button"
          disabled={page >= totalPages}
          aria-label={text('Επόμενη σελίδα', 'Next page')}
          className={`${btnSecondary} !px-2.5 !py-1.5`}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          ›
        </button>
      </nav>
    </div>
  );
}

export type CursorPaginationProps = {
  hasNext: boolean;
  onNext: () => void;
  onFirst?: () => void;
  isFirst?: boolean;
  className?: string;
};

/** Cursor-based lists (API nextCursor) — same chrome as ListPagination. */
export function CursorPagination(props: CursorPaginationProps) {
  const { text } = useAdminLocale();
  return (
    <div
      className={`mt-auto flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-white px-4 py-3 sm:px-5 dark:border-slate-800 dark:bg-slate-900 ${props.className ?? ''}`}
    >
      <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
        {text('Σελίδα αποτελεσμάτων από τον διακομιστή', 'Server results page')}
      </p>
      <div className="flex flex-wrap gap-2">
        {props.onFirst ? (
          <button
            type="button"
            disabled={props.isFirst !== false}
            className={btnSecondary}
            onClick={props.onFirst}
          >
            {text('Πρώτη σελίδα', 'First page')}
          </button>
        ) : null}
        <button
          type="button"
          disabled={!props.hasNext}
          className={btnSecondary}
          onClick={props.onNext}
        >
          {text('Επόμενη σελίδα', 'Next page')} ›
        </button>
      </div>
    </div>
  );
}

export function useClientPaging<T>(
  rows: readonly T[],
  options?: { initialPageSize?: number; resetKey?: string | number | null },
): {
  page: number;
  totalPages: number;
  total: number;
  itemsPerPage: number;
  rows: T[];
  setPage: (page: number) => void;
  pager: ListPaginationProps;
} {
  const prefs = useUiPrefs();
  const itemsPerPage = options?.initialPageSize ?? prefs.tablePageSize;
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [options?.resetKey, rows.length, itemsPerPage]);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const slice = useMemo(
    () => rows.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [rows, safePage, itemsPerPage],
  );

  return {
    page: safePage,
    totalPages,
    total,
    itemsPerPage,
    rows: [...slice],
    setPage,
    pager: {
      page: safePage,
      totalPages,
      total,
      itemsPerPage,
      onPageChange: setPage,
    },
  };
}

function visiblePages(page: number, totalPages: number): Array<number | '…'> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const set = new Set<number>(
    [1, totalPages, page, page - 1, page + 1].filter((p) => p >= 1 && p <= totalPages),
  );
  const sorted = [...set].sort((a, b) => a - b);
  const out: Array<number | '…'> = [];
  for (const value of sorted) {
    const last = out[out.length - 1];
    if (typeof last === 'number' && value - last > 1) out.push('…');
    out.push(value);
  }
  return out;
}

export function PagedTableShell(props: {
  children: ReactNode;
  pagination: ListPaginationProps | null;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-x-auto">{props.children}</div>
      {props.pagination && props.pagination.total > 0 ? (
        <ListPagination {...props.pagination} />
      ) : null}
    </div>
  );
}
